import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PencilSimpleIcon, FloppyDiskIcon } from '@phosphor-icons/react';
import { useUpdateStagedTransaction } from '@/api/rpc/hooks/ship-and-debit/useE844sdTransactions';
import { useCan } from '@/hooks/useCan';
import { Action, Subject } from '@erp/authz';
import { useToast } from 'src/context/ToastContext';

interface PostedUpcEditorProps {
  transactionId: string;
  currentUpc: string;
}

/**
 * Posted (GL-posted) transactions are frozen — EXCEPT for UPC. `upc_code` is a
 * pure identifier that doesn't affect the claim amount or GL posting, so it can
 * be filled in when source ITEMAST was missing it and the "UPC required" rule
 * held the claim back. The backend (updateStagedTransactionProcedure) enforces
 * the UPC-only restriction; this editor just exposes it without an
 * un-post/re-post round trip.
 *
 * Kept as its own small component (rather than inline in TransactionViewModal)
 * so the big modal stays lean. Split into view/edit rows so each piece stays
 * simple. The parent remounts it per transaction via a `key`, so no
 * cross-transaction reset bookkeeping is needed here.
 */
export function PostedUpcEditor({
  transactionId,
  currentUpc,
}: PostedUpcEditorProps) {
  const [canEdit] = useCan(Action.Update, Subject.SDTransaction);
  const [editing, setEditing] = useState(false);

  if (!canEdit) return null;

  return (
    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-amber-800">
          <T context="label for the UPC code field on a posted transaction">
            UPC Code
          </T>
        </span>
        {editing ? (
          <UpcEditRow
            transactionId={transactionId}
            currentUpc={currentUpc}
            onClose={() => setEditing(false)}
          />
        ) : (
          <UpcViewRow currentUpc={currentUpc} onEdit={() => setEditing(true)} />
        )}
      </div>
      <p className="mt-1 text-[11px] text-amber-700">
        <T context="explanation of why UPC can be edited on an otherwise-frozen posted transaction">
          This transaction is posted and frozen, but its UPC can still be filled
          in — UPC doesn&rsquo;t affect the claim amount or GL posting.
        </T>
      </p>
    </div>
  );
}

function UpcViewRow({
  currentUpc,
  onEdit,
}: {
  currentUpc: string;
  onEdit: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="truncate font-mono text-xs text-amber-900">
        {currentUpc || '—'}
      </span>
      <Button
        variant="outline"
        size="sm"
        className="h-7 text-xs"
        onClick={onEdit}
      >
        <PencilSimpleIcon className="mr-1 h-3.5 w-3.5" />
        <T context="button label to edit the UPC on a posted transaction">
          Edit UPC
        </T>
      </Button>
    </div>
  );
}

function UpcEditRow({
  transactionId,
  currentUpc,
  onClose,
}: {
  transactionId: string;
  currentUpc: string;
  onClose: () => void;
}) {
  const gt = useGT();
  const [draft, setDraft] = useState(currentUpc);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const updateMutation = useUpdateStagedTransaction();

  const save = () => {
    const next = draft.trim();
    if (next === currentUpc) {
      onClose();
      return;
    }
    setError(null);
    updateMutation.mutate(
      { transactionId, updates: { upc_code: next || null } },
      {
        onSuccess: () => {
          showToast({
            message: gt('UPC updated successfully', {
              context: 'success toast message after saving a UPC code',
            }),
            title: gt('Success', { context: 'success toast title' }),
            color: 'success',
            icon: 'check',
          });
          queryClient.invalidateQueries({
            queryKey: [
              `rexel/ship-and-debit/844/transactions/${transactionId}`,
            ],
          });
          onClose();
        },
        onError: (err: Error) => setError(err.message),
      },
    );
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="h-7 w-48 font-mono text-xs"
        placeholder={gt('Enter UPC', {
          context: 'placeholder for the UPC code input',
        })}
        autoFocus
      />
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs"
        onClick={onClose}
        disabled={updateMutation.isPending}
      >
        <T context="button label to cancel editing">Cancel</T>
      </Button>
      <Button
        size="sm"
        className="h-7 text-xs"
        onClick={save}
        disabled={updateMutation.isPending}
      >
        <FloppyDiskIcon className="mr-1 h-3.5 w-3.5" />
        <T context="button label to save changes">Save</T>
      </Button>
      {error && <span className="text-[11px] text-red-700">{error}</span>}
    </div>
  );
}
